import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Skeleton
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/currency';

const MetricCard = ({
  title,
  value,
  trend,
  trendValue,
  icon,
  color = 'primary',
  progress,
  subtitle,
  format = 'default',
  loading = false
}) => {
  const theme = useTheme();
  const paletteEntry = theme.palette[color] || theme.palette.primary;
  const accentColor = paletteEntry.main;
  const accentBackground = alpha(accentColor, 0.1);

  const isTrendPositive = trend === 'up';
  const TrendIcon = isTrendPositive ? TrendingUpIcon : TrendingDownIcon;
  const trendAccent = isTrendPositive ? theme.palette.success.main : theme.palette.error.main;
  const trendBackground = alpha(trendAccent, 0.12);

  const formatValue = (val) => {
    if (format === 'currency') {
      return formatCurrency(val || 0);
    } else if (format === 'percentage') {
      return `${val}%`;
    } else if (format === 'fraction') {
      return val;
    }
    return Number(val || 0).toLocaleString();
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        backgroundColor: 'background.paper'
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="text.secondary" variant="overline" sx={{ letterSpacing: 0.6 }}>
              {title}
            </Typography>
            {loading ? (
              <Box sx={{ mt: 0.5 }}>
                <Skeleton variant="text" width={120} height={36} />
                {subtitle && <Skeleton variant="text" width={80} height={18} />}
              </Box>
            ) : (
              <>
                <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
                  {formatValue(value)}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: accentBackground,
                color: accentColor,
                flexShrink: 0
              }}
            >
              {React.cloneElement(icon, { fontSize: 'small' })}
            </Box>
          )}
        </Box>

        {!loading && trend && trendValue && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: trendBackground,
              color: trendAccent,
              fontSize: '0.75rem',
              fontWeight: 600,
              width: 'fit-content'
            }}
          >
            <TrendIcon sx={{ fontSize: '0.9rem' }} />
            {trendValue}
          </Box>
        )}

        {!loading && progress !== undefined && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={color}
              sx={{ height: 6, borderRadius: 1.5, backgroundColor: alpha(accentColor, 0.08) }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
